export class TableUtils {
  /**
   * Basic descending comparator for generic values
   */
  descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    const sortA = a[orderBy] || '';
    const sortB = b[orderBy] || '';
    if (sortB < sortA) {
      return -1;
    }
    if (sortB > sortA) {
      return 1;
    }
    return 0;
  }

  /**
   * String comparator that handles null/undefined values
   */
  static StringComparator(a: any, b: any, isDescending: boolean): number {
    // Handle null/undefined values
    if (a === null || a === undefined) return isDescending ? 1 : -1;
    if (b === null || b === undefined) return isDescending ? -1 : 1;
    
    // Convert to strings and compare
    const strA = String(a).toLowerCase();
    const strB = String(b).toLowerCase();
    const result = strA.localeCompare(strB);
    return isDescending ? result * -1 : result;
  }

  /**
   * Number comparator that handles null/undefined values
   */
  static NumberComparator(a: any, b: any, isDescending: boolean): number {
    // Handle null/undefined values
    if (a === null || a === undefined) return isDescending ? 1 : -1;
    if (b === null || b === undefined) return isDescending ? -1 : 1;
    
    // Convert to numbers
    const numA = Number(a);
    const numB = Number(b);
    
    // Handle NaN values
    if (isNaN(numA)) return isDescending ? 1 : -1;
    if (isNaN(numB)) return isDescending ? -1 : 1;
    
    const result = numA - numB;
    return isDescending ? result * -1 : result;
  }

  /**
   * Boolean comparator that handles null/undefined values
   */
  static BooleanComparator(a: any, b: any, isDescending: boolean): number {
    // Handle null/undefined values
    if (a === null || a === undefined) return isDescending ? 1 : -1;
    if (b === null || b === undefined) return isDescending ? -1 : 1;
    
    // Convert to booleans (true sorts after false)
    const boolA = Boolean(a);
    const boolB = Boolean(b);
    
    if (boolA === boolB) return 0;
    
    const result = boolA ? 1 : -1;
    return isDescending ? result * -1 : result;
  }

  /**
   * Date comparator that handles null/undefined values and invalid dates
   */
  static DateComparator(a: any, b: any, isDescending: boolean): number {
    // Handle null/undefined values
    if (a === null || a === undefined) return isDescending ? 1 : -1;
    if (b === null || b === undefined) return isDescending ? -1 : 1;
    
    // Convert to Date objects
    const dateA = new Date(a);
    const dateB = new Date(b);
    
    // Handle invalid dates
    if (isNaN(dateA.getTime())) return isDescending ? 1 : -1;
    if (isNaN(dateB.getTime())) return isDescending ? -1 : 1;
    
    const result = dateA.getTime() - dateB.getTime();
    return isDescending ? result * -1 : result;
  }

  /**
   * Get appropriate comparator based on data type
   */
  getTypedComparator(dataType: string, order: Order): (a: any, b: any) => number {
    const isDescending = order === 'desc';
    
    switch (dataType) {
      case 'number':
        return (a, b) => TableUtils.NumberComparator(a, b, isDescending);
      case 'boolean':
        return (a, b) => TableUtils.BooleanComparator(a, b, isDescending);
      case 'date':
        return (a, b) => TableUtils.DateComparator(a, b, isDescending);
      case 'string':
      default:
        return (a, b) => TableUtils.StringComparator(a, b, isDescending);
    }
  }

  /**
   * Get comparator function based on order, field, data type, and custom sort function
   */
  getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
    dataType?: string,
    customSort?: (a: any, b: any, order: Order) => number
  ): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    // If custom sort function is provided, use it
    if (customSort) {
      // @ts-ignore todo START HERE getNestedValue may be in the wrong spot
      return (a, b) => customSort(getNestedValue(a, orderBy), getNestedValue(b, orderBy), order);
    }
    
    // If data type is provided, use typed comparator
    if (dataType) {
      const typedComparator = this.getTypedComparator(dataType, order);
      // @ts-ignore
      return (a, b) => typedComparator(getNestedValue(a, orderBy), getNestedValue(b, orderBy));
    }
    
    // @ts-ignore Fall back to default comparator
    return (a, b) => TableUtils.StringComparator(getNestedValue(a, orderBy), getNestedValue(b, orderBy), order === 'desc');
  }

  /**
   * Stable sort implementation that preserves original order for equal values
   */
  stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }
}

export type Order = 'asc' | 'desc';

/**
 * Retrieve the value of a nested property within an object based on a dot-notated string path.
 *
 * @param obj - The object to traverse.
 * @param path - A string representing the property path using dot notation.
 * @return The value at the specified path, or `undefined` if the path is invalid or doesn't exist.
 */
export function getNestedValue(obj: Record<string, any>, path: string): any {
  if (!path) return obj; // If the path is empty, return the object itself
  return path?.split('.')?.reduce((acc, key) => acc && acc[key], obj);
}